import { useFormik } from "formik";
import i18next from "i18next";
import React, { useEffect, useState } from "react";
import { useDeliveryTimes } from "../../Api/deliveryTimesApi";
import { getLocalisedDayOfWeek, readableDate } from "../../Helpers/date-utils";
import Button, { ForwardButton } from "../Button";
import Radio from "../Forms/Radio";
import { SkeletonTimeSlots } from "../Loaders/SkeletonLoader";
import Popup from "../Popup";
import Timeslot from "../Timeslot";
import Headline from "../Typography/Headline";
import { TimeslotPopupPropTypes } from "./types";

const STEPS = {
    DAY_SELECT: "DAY_SELECT",
    SLOT_SELECT: "SLOT_SELECT",
    EARLY_ON_LOCATION: "EARLY_ON_LOCATION",
};

const nextStepsMap = {
    [STEPS.DAY_SELECT]: STEPS.SLOT_SELECT,
    [STEPS.SLOT_SELECT]: STEPS.EARLY_ON_LOCATION,
    [STEPS.EARLY_ON_LOCATION]: null,
};

const TimeslotPopup = ({
    popupOpen,
    setOpen,
    handleSubmit = () => null,
    firstAvailableId = null,
    buffer = false,
}) => {
    const { deliveryTimes, isLoading, refetchTimeslots } = useDeliveryTimes({
        buffer,
    });

    useEffect(() => {
        if (popupOpen) {
            refetchTimeslots();
        }
    }, [popupOpen]);

    const [step, setStep] = useState(STEPS.DAY_SELECT);
    const formik = useFormik({
        initialValues: {
            day: null,
            slot: null,
            earlyOnLocation: true,
        },
        onSubmit: ({ slot, earlyOnLocation }) => {
            handleSubmit({ slot, earlyOnLocation });
            setStep(STEPS.DAY_SELECT); // reset
            setOpen(false);
        },
    });

    const handleNextClick = () => {
        const nextStep = nextStepsMap[step];
        const cookie = window.cookie.get();

        const isPickup = cookie?.area?.type === "pickup";
        const shouldSkipEarlyOnLocation =
            isPickup && nextStep === STEPS.EARLY_ON_LOCATION;

        if (nextStep === null || shouldSkipEarlyOnLocation) {
            formik.submitForm();
            return;
        }

        setStep(nextStep);
    };

    const renderPopupContent = () => {
        switch (step) {
            case STEPS.EARLY_ON_LOCATION:
                return renderEarlyOnLocation();
            case STEPS.SLOT_SELECT:
                return renderTimeslots();
            case STEPS.DAY_SELECT:
                return renderDaySelect();
            default:
                setOpen(false);
        }
    };

    const forwardDisabledCheck = () => {
        const { day, slot } = formik.values;
        if (step === STEPS.DAY_SELECT && day === null) {
            return true;
        }

        if (step === STEPS.SLOT_SELECT && slot === null) {
            return true;
        }

        return false;
    };

    const renderDaySelect = () => {
        const cookie = window.cookie.get();
        const isPickup = cookie?.area?.type === "pickup";
        const title = isPickup
            ? "timeslotPopup.title"
            : "timeslotPopup.titleDelivery";

        return (
            <div>
                <Headline size={6} className="timeslot-popup__title">
                    {i18next.t(title)}
                </Headline>
                <div className="timeslot-popup__content">
                    {Object.keys(deliveryTimes).map((day) => {
                        // Handles edge case when only availabe timeslot for today is also the firstAvailable
                        const includesOnlyFirstAvailable = !deliveryTimes[
                            day
                        ].filter((slot) => slot.id !== firstAvailableId).length;

                        if (includesOnlyFirstAvailable) {
                            return null;
                        }

                        return (
                            <Radio
                                onValueChange={() =>
                                    formik.setFieldValue("day", day)
                                }
                                bindName="daySelect"
                                value={day}
                                customStyles="day-select"
                                key={`day-select-${day}`}
                                currentValue={formik.values.day}>
                                <p>
                                    <span className="append-bold">
                                        {getLocalisedDayOfWeek(day)}
                                    </span>
                                    <span>, den {readableDate(day)}</span>
                                </p>
                            </Radio>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderTimeslots = () => {
        const { day } = formik.values;

        const timeslots = deliveryTimes[day].filter(
            (slot) => slot.id !== firstAvailableId
        );

        const cookie = window.cookie.get();
        const isPickup = cookie?.area?.type === "pickup";
        const title = isPickup
            ? "timeslotPopup.slotPickTitle"
            : "timeslotPopup.deliverySlotTitle";

        return (
            <div>
                <Headline size={6} className="timeslot-popup__title">
                    {i18next.t(title)}
                </Headline>
                <p className="timeslot-popup__subtitle">
                    {`${getLocalisedDayOfWeek(day)}, den ${readableDate(day)}`}
                </p>
                <div className="timeslot-popup__content timeslot-popup__content--slots">
                    {timeslots.map((timeslot, idx) => (
                        <Timeslot
                            key={`timeslot-${idx}`}
                            time={timeslot}
                            defaultValue={formik.values.slot}
                            availability={timeslot.capacity}
                            onValueChange={() =>
                                formik.setFieldValue("slot", timeslot)
                            }
                        />
                    ))}
                </div>
            </div>
        );
    };

    const renderEarlyOnLocation = () => {
        const { earlyOnLocation, day } = formik.values;
        return (
            <div>
                <Headline size={6} className="timeslot-popup__title">
                    {i18next.t("timeslotPopup.willBeEarly")}
                </Headline>
                <p className="timeslot-popup__subtitle">
                    {`${getLocalisedDayOfWeek(day)}, den ${readableDate(day)}`}
                </p>
                <div className="timeslot-popup__content">
                    <Radio
                        value={true}
                        bindName="earlyOnLocation"
                        currentValue={earlyOnLocation}
                        onValueChange={() =>
                            formik.setFieldValue("earlyOnLocation", true)
                        }>
                        <p className="timeslot-popup__option">
                            {i18next.t("timeslotPopup.yesEarly")}
                        </p>
                    </Radio>
                    <Radio
                        value={false}
                        bindName="earlyOnLocation"
                        currentValue={earlyOnLocation}
                        onValueChange={() =>
                            formik.setFieldValue("earlyOnLocation", false)
                        }>
                        <p className="timeslot-popup__option">
                            {i18next.t("timeslotPopup.noEarly")}
                        </p>
                    </Radio>
                </div>
            </div>
        );
    };

    return (
        <Popup
            {...{
                open: popupOpen,
                isClosable: true,
                handleClose: () => {
                    setOpen(false);
                    setStep(STEPS.DAY_SELECT);
                },
                customStyle: "timeslot-popup",
            }}>
            {isLoading ? <SkeletonTimeSlots /> : renderPopupContent()}
            {step === STEPS.EARLY_ON_LOCATION ? (
                <Button
                    size="SM"
                    label={i18next.t("timeslotPopup.finish")}
                    onClick={handleNextClick}
                    customStyle="timeslot-popup__btn"
                />
            ) : (
                <ForwardButton
                    disabled={forwardDisabledCheck()}
                    onClick={handleNextClick}
                    size="SM"
                    label={i18next.t("timeslotPopup.continue")}
                    variant={"secondary"}
                    customStyle="timeslot-popup__btn"
                />
            )}
        </Popup>
    );
};

TimeslotPopup.propTypes = TimeslotPopupPropTypes;
export default TimeslotPopup;
